import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Lock } from "lucide-react";
import { getAdminToken } from "@/lib/api";

const navItems = [
    { to: "/about", label: "About", testId: "nav-about" },
    { to: "/#for-reps", label: "For Reps", testId: "nav-for-reps-link" },
    { to: "/for-surgeons", label: "For Surgeons", testId: "nav-for-surgeons" },
    { to: "/inducext", label: "InduceXT", testId: "nav-products" },
    { to: "/for-reps", label: "Schedule a Call", testId: "nav-for-reps" },
    { to: "/faq", label: "FAQ", testId: "nav-faq" },
    { to: "/contact", label: "Contact", testId: "nav-contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    const goJoinRep = () => {
        setOpen(false);
        navigate("/#join");
    };

    return (
        <header
            data-testid="site-navbar"
            className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-200"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="brand-logo"
                    className="font-display text-xl font-semibold tracking-tight text-slate-900 flex items-center gap-2"
                >
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-900" />
                    1099er
                    <span
                        data-testid="brand-logo-beta"
                        className="ml-1 text-[10px] font-medium uppercase tracking-[0.14em] text-slate-500 border border-slate-300 rounded-full px-1.5 py-0.5"
                    >
                        Beta
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-8">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={item.testId}
                            end={item.to === "/"}
                            className={({ isActive }) =>
                                `text-sm font-medium transition-colors ${
                                    isActive
                                        ? "text-slate-900"
                                        : "text-slate-500 hover:text-slate-900"
                                }`
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    {getAdminToken() && (
                        <button
                            data-testid="nav-admin-login-btn"
                            onClick={() => navigate("/admin")}
                            className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 border border-slate-200 hover:border-slate-400 px-3 py-2 rounded-md transition-colors"
                        >
                            <Lock size={14} />
                            Admin
                        </button>
                    )}
                    <button
                        data-testid="nav-join-rep-btn"
                        onClick={goJoinRep}
                        className="bg-slate-900 text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-slate-800 transition-colors"
                    >
                        Schedule a Confidential Call
                    </button>
                </div>

                <button
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen(!open)}
                    className="lg:hidden text-slate-900 p-2"
                    aria-label="Toggle navigation"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden border-t border-slate-200 bg-white"
                >
                    <div className="px-6 py-4 flex flex-col gap-4">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                data-testid={`${item.testId}-mobile`}
                                end={item.to === "/"}
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    `text-base font-medium ${
                                        isActive ? "text-slate-900" : "text-slate-600"
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                        <button
                            data-testid="nav-join-rep-btn-mobile"
                            onClick={goJoinRep}
                            className="bg-slate-900 text-white text-sm font-medium px-4 py-2.5 rounded-md hover:bg-slate-800 transition-colors mt-2"
                        >
                            Schedule a Confidential Call
                        </button>
                        {getAdminToken() && (
                            <button
                                data-testid="nav-admin-login-btn-mobile"
                                onClick={() => {
                                    setOpen(false);
                                    navigate("/admin");
                                }}
                                className="flex items-center justify-center gap-1.5 text-sm font-medium text-slate-600 border border-slate-200 px-4 py-2.5 rounded-md hover:text-slate-900 transition-colors"
                            >
                                <Lock size={14} />
                                Admin Login
                            </button>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
